import React, { useState } from 'react';
import { Printer, Loader2, Tag, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PrintMethodToggle from '@/components/print/PrintMethodToggle';
import { UsnPrinterSettingsDialog } from '@/components/print/UsnPrinterSettingsDialog';
import { useUsnStickerPrint } from '@/hooks/useUsnStickerPrint';
import { toast } from 'sonner';

// Reusable USN-sticker print block for the Job Order UIs (Inhouse / Flexi /
// Tissue / Shirting). Uses the SAME aligned pipeline as Folding Print &
// Segregation (shared label configuration + Print Bridge routing) so every
// sticker prints with the correct QR + USN + optional "Sort:" alignment.
export const UsnStickerPrinter = ({ groups = [], screenKey = 'jw-usn', noun = 'USN sticker', groupNoun = 'bundle', unit = 'USN' }) => {
  // Shares the folding/segregation printer setup (label config + USB device)
  // via the default storageKey, so operators need zero re-setup.
  const print = useUsnStickerPrint({ screenKey });
  const [printingKey, setPrintingKey] = useState(null);

  const slug = (s) => String(s || '').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'ungrouped';

  // Flatten every group's USNs, carrying each USN's bundle sort_no so the
  // "Sort:" line matches the reference sticker.
  const allUsns = [];
  const allSorts = [];
  groups.forEach((g) => (g.usns || []).filter(Boolean).forEach((u) => {
    allUsns.push(u);
    allSorts.push(g.sort_no || '');
  }));

  const doPrint = async (usns, sortNos, key) => {
    const list = (usns || []).filter(Boolean);
    if (!list.length) { toast.error('No USNs to print'); return; }
    setPrintingKey(key);
    try {
      const n = await print.printUsns(list, { sortNos });
      toast.success(`Sent ${n} USN sticker(s) to ${print.printDestination}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Print failed');
    } finally {
      setPrintingKey(null);
    }
  };

  return (
    <div data-testid="usn-sticker-printer" className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Tag className="w-4 h-4 text-indigo-600" />
          <span data-testid="usn-printer-summary">{allUsns.length} {noun}(s) · {groups.length} {groupNoun}(s)</span>
        </div>
        <div className="flex items-center gap-2">
          <PrintMethodToggle screenKey={screenKey} autoNetworkWhenRouted onMethodChange={print.setPrintMethod} />
          <Button size="sm" variant={print.ready ? 'outline' : 'default'}
            className={print.ready ? '' : 'bg-amber-500 hover:bg-amber-600 text-white'}
            onClick={() => print.setSettingsOpen(true)} data-testid="usn-printer-settings-btn">
            <Settings className="w-4 h-4 mr-1.5" />
            {print.ready ? 'Printer' : 'Setup'}
          </Button>
          <Button size="sm" className="bg-indigo-700 hover:bg-indigo-800"
            disabled={!allUsns.length || printingKey !== null}
            onClick={() => doPrint(allUsns, allSorts, '__all__')} data-testid="print-all-usns-btn">
            {printingKey === '__all__' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
            Print All ({allUsns.length})
          </Button>
        </div>
      </div>
      <div className="border rounded-lg divide-y max-h-72 overflow-y-auto">
        {groups.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6" data-testid="usn-printer-empty">No USNs available to print yet</p>
        ) : groups.map((g) => (
          <div key={g.bundle_code} className="flex items-center justify-between px-3 py-2" data-testid={`usn-group-${slug(g.bundle_code)}`}>
            <div className="min-w-0">
              <p className="font-mono text-sm text-gray-800 truncate">{g.bundle_code}</p>
              <p className="text-[11px] text-gray-500">
                {(g.usns || []).length} {unit}(s){g.design_name ? ` · ${g.design_name}` : ''}{g.shade ? ` · ${g.shade}` : ''}{g.sort_no ? ` · Sort ${g.sort_no}` : ''}
              </p>
            </div>
            <Button size="sm" variant="outline" disabled={!(g.usns || []).length || printingKey !== null}
              onClick={() => doPrint(g.usns, (g.usns || []).map(() => g.sort_no || ''), g.bundle_code)}
              data-testid={`print-bundle-usns-${slug(g.bundle_code)}`}>
              {printingKey === g.bundle_code ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Printer className="w-3.5 h-3.5 mr-1" /> Reprint</>}
            </Button>
          </div>
        ))}
      </div>
      <UsnPrinterSettingsDialog open={print.settingsOpen} onOpenChange={print.setSettingsOpen} print={print} />
    </div>
  );
};

export default UsnStickerPrinter;
